import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { cast } from 'mobx-state-tree'

import { useStores, ISelectorOption } from '@trivie/core'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'

import { AutocompleteSelector } from '../../../../../components/autocomplete-selector'

interface AssignmentUsersDialogProps {
  open: boolean
  requestClose: () => void
}

export const AssignmentUsersDialog = observer((props: AssignmentUsersDialogProps) => {
  const { open, requestClose } = props
  const { surveyStore } = useStores()
  const { selectedSurvey, updateSurveyAssignees, getSurveyDetails } = surveyStore

  const [selected, setSelected] = useState<ISelectorOption[]>([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      setSelected([])
    }
  }, [open])

  const filterExcludeIds = [
    ...(selectedSurvey?.userIds.map((id) => ({ id, type: 'Users' })) ?? []),
    ...(selectedSurvey?.groupIds.map((id) => ({ id, type: 'Groups' })) ?? []),
  ]

  const onConfirm = () => {
    if (!selectedSurvey?.id || submitting) return
    const newGroupIds = selected.filter((s) => s.type === 'Groups').map((s) => s.id)
    const newUserIds = selected.filter((s) => s.type === 'Users').map((s) => s.id)
    const payload = {
      group_ids: [...(selectedSurvey.groupIds ?? []), ...newGroupIds],
      user_ids: [...(selectedSurvey.userIds ?? []), ...newUserIds],
    }
    setSubmitting(true)
    updateSurveyAssignees(selectedSurvey.id, cast(payload))
      .then(() => {
        if (selectedSurvey?.id) {
          return getSurveyDetails(selectedSurvey.id)
        }
        return undefined
      })
      .finally(() => {
        setSubmitting(false)
        requestClose()
      })
  }

  return (
    <Dialog
      maxWidth="md"
      onCancel={requestClose}
      onClose={requestClose}
      open={open}
      title="Add Users"
      onConfirm={onConfirm}
      confirmBtnLabel="Add"
      disableConfirm={selected.length === 0 || submitting}
    >
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
        <AutocompleteSelector
          variant="users-groups"
          open
          placeholder="Search users and groups"
          filterExcludeIds={filterExcludeIds}
          onSelectionChange={setSelected}
        />
      </div>
    </Dialog>
  )
})
